import { AppFileRegistry } from '../services/AppFileRegistry';
import { AuthService } from '../services/AuthService';
import { GitHubService } from '../services/GitHubService';
import { MarkdownService } from '../services/MarkdownService';
import { NavOrderService } from '../services/NavOrderService';
import { NewsFeedConfigService } from '../services/NewsFeedConfigService';
import { ShareDecryptView } from '../components/ShareDecryptView';
import { AuthScreen } from '../components/AuthScreen';
import { readShareLinkFromLocation, type ShareLinkFromLocation } from '../utils/shareLinkCrypto';
import { Sidebar } from '../components/Sidebar';
import { SidebarSearch } from '../components/SidebarSearch';
import { FileView } from '../components/FileView';
import type { HitMeta } from '../search/contentSearch';
import type { Bm25Index } from '../search/bm25';
import { ShareEncryptToolView } from '../components/ShareEncryptToolView';
import { ConfigView } from '../components/ConfigView';
import { ConfigImportView } from '../components/ConfigImportView';
import { NewsView } from '../components/NewsView';
import { WeatherView } from '../components/WeatherView';
import { EarTrainingView } from '../components/EarTrainingView';
import { ChessView } from '../components/ChessView';
import { CursorAgentView } from '../components/CursorAgentView';
import { hasAuthUrlPrefill } from '../utils/authUrlParams';
import { mountGitBusyOverlay } from '../utils/gitBusy';
import { mountAppDialog } from '../utils/appDialog';
import { mountImportMarkdownModal } from '../utils/importMarkdownModal';
import type { DatabaseFile, GitHubConfig, NavOrderFile } from '../types';
import { mountHttpDebugModal } from '../utils/httpDebugLog';
import { mountMediaDebugModal, setMediaDebugEnabled } from '../utils/mediaDebugLog';
import { mountSaveDebugModal } from '../utils/saveDebugLog';
import { AppAudioCache } from './appAudio';
import { AppVideoCache } from './appVideo';
import { AppImageCache } from './appImages';
import type { AppContext } from './appContext';
import * as appViews from './appViews';
import { mountAppShell } from './appShell';
import * as fileLifecycle from './fileLifecycle';
import { emptyPendingItemMoveState } from './pendingItemMoves';

export class App implements AppContext {
  readonly root: HTMLElement;
  github!: GitHubService;
  readonly markdown = new MarkdownService();
  sidebar!: Sidebar;
  sidebarSearch!: SidebarSearch;
  fileView!: FileView;
  searchIndex: Bm25Index<HitMeta> | null = null;
  searchIndexBuild: Promise<Bm25Index<HitMeta>> | null = null;
  readonly shareEncryptToolView = new ShareEncryptToolView();
  readonly configView = new ConfigView();
  readonly configImportView = new ConfigImportView();
  readonly weatherView = new WeatherView();
  readonly newsView = new NewsView();
  readonly earTrainingView = new EarTrainingView();
  readonly chessView = new ChessView();
  readonly cursorAgentView = new CursorAgentView();
  dailyDashboardAvailable = false;
  fileRegistry!: AppFileRegistry;
  navOrder!: NavOrderService;
  newsFeedConfig!: NewsFeedConfigService;
  allPaths: string[] = [];
  pendingItemMoves = emptyPendingItemMoveState();
  dashboardBlobUrl: string | null = null;
  private readonly imageCache = new AppImageCache();
  private readonly audioCache = new AppAudioCache();
  private readonly videoCache = new AppVideoCache();

  get navOrderFile(): NavOrderFile {
    return this.navOrder.getNavOrder();
  }
  set navOrderFile(next: NavOrderFile) {
    this.navOrder.setNavOrder(next);
  }

  constructor(root: HTMLElement) {
    this.root = root;
  }

  start(): void {
    if (this.tryShowShareDecryptFromLocation()) return;

    const urlAuth = hasAuthUrlPrefill();
    if (urlAuth) {
      AuthService.clear();
    }

    const config = AuthService.load();
    if (config && !urlAuth) {
      this.init(config);
      return;
    }

    new AuthScreen(cfg => this.init(cfg)).render(this.root);
  }

  private init(config: GitHubConfig): void {
    mountGitBusyOverlay();
    mountAppDialog();
    mountImportMarkdownModal();
    mountHttpDebugModal();
    mountMediaDebugModal();
    mountSaveDebugModal();
    if (new URLSearchParams(window.location.search).has('mediaDebug')) {
      setMediaDebugEnabled(true);
    }
    this.github = new GitHubService(config);
    this.fileRegistry = new AppFileRegistry(this.github, this.markdown);
    this.navOrder = new NavOrderService(this.github);
    this.newsFeedConfig = new NewsFeedConfigService(this.github);

    const mdPath = () => this.fileView?.getCurrentFilePath() ?? undefined;
    const shell = mountAppShell(
      this,
      this.github,
      (repoPath, fullSize, bustCache) =>
        this.imageCache.getDisplayUrl(this.github, repoPath, fullSize, bustCache, mdPath()),
      (repoPath, bustCache) =>
        this.audioCache.getPlaybackUrl(this.github, repoPath, bustCache, mdPath()),
      (repoPath, bustCache, opts) =>
        this.videoCache.getPlaybackUrl(this.github, repoPath, bustCache, mdPath(), opts)
    );
    this.sidebar = shell.sidebar;
    this.sidebarSearch = shell.sidebarSearch;
    this.fileView = shell.fileView;

    void fileLifecycle.loadFiles(this);

    window.addEventListener('popstate', () => {
      this.tryShowShareDecryptFromLocation();
    });
  }

  /** When URL has `?s=`, show public decrypt UI (same as opening a copied share link). */
  tryShowShareDecryptFromLocation(): boolean {
    const shareLink = readShareLinkFromLocation();
    if (!shareLink) return false;
    this.showShareDecrypt(shareLink);
    return true;
  }

  private showShareDecrypt(shareLink: ShareLinkFromLocation): void {
    this.revokeDashboardBlob();
    this.revokeImageObjectUrls();
    ShareDecryptView.render(this.root, shareLink.payload, shareLink.key);
  }

  invalidateSearchIndex(): void {
    this.searchIndex = null;
    this.searchIndexBuild = null;
  }

  revokeDashboardBlob(): void {
    appViews.revokeDashboardBlob(this);
  }

  revokeImageObjectUrls(): void {
    this.imageCache.revokeAll();
    this.audioCache.revokeAll();
    this.videoCache.revokeAll();
  }

  async refreshDailyDashboardAvailability(): Promise<void> {
    return fileLifecycle.refreshDailyDashboardAvailability(this);
  }

  renderDailyDashboard(mainEl: HTMLElement, html: string): void {
    appViews.renderDailyDashboard(this, mainEl, html);
  }

  async loadFiles(): Promise<void> {
    return fileLifecycle.loadFiles(this);
  }

  async saveFile(file: DatabaseFile): Promise<void> {
    return fileLifecycle.saveFile(this, file);
  }
}
